import React from 'react';
import { t } from '@citrite/translate';
import {
	IInCircleSolidIcon,
	layout,
	palette,
	Text,
	typography,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { SkeletonLoaderShapes } from '@citrite/workspace-ui-platform-react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import moment from 'moment';
import { SummaryState } from 'App/Activity/Network/ActionsUtil';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { ResourceMenu } from 'App/Tile/ResourceMenu';
import { useResourceContextMenuBuilder } from 'App/Tile/useResourceContextMenuBuilder';
import { detectLanguage } from 'javascript/sf/detectLanguage';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

const { LoadingBar } = SkeletonLoaderShapes;

const TextWithFallback = styled(Text)`
	&:empty::after {
		content: '\u00A0';
		visibility: hidden;
	}
`;
const FooterWrapper = styled.div<{ shouldGrayOut?: boolean }>`
	height: 140px;
	padding: ${layout.mediumSpace};
	display: flex;
	flex-direction: column;

	${({ shouldGrayOut }) =>
		shouldGrayOut &&
		css`
			& ${TitleText}, {
				color: ${palette.grey700};
			}

			& ${SubTitleText} {
				color: ${palette.grey600};
			}
		`}
`;
const TitleText = styled(TextWithFallback)`
	font-size: ${typography.sizes.base};
	font-weight: ${typography.weights.regular};
	color: ${palette.black};
	word-wrap: break-word;
`;

const SubTitleText = styled(TextWithFallback)`
	font-size: ${typography.sizes.sml2};
	font-weight: ${typography.weights.semiBold};
	color: ${palette.grey800};
	margin-top: ${layout.smallSpace};
`;
const LastOpenedText = styled(TextWithFallback)`
	font-size: ${typography.sizes.xsml};
	font-weight: ${typography.weights.regular};
	color: ${palette.grey700};
	margin-top: ${layout.space14};
`;

const MaintenanceWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${layout.space4};
	margin-top: auto;
`;

const MaintenanceModeReason = styled(Text)`
	font-size: ${typography.sizes.xsml};
	color: ${palette.grey900};
	font-weight: ${typography.weights.regular};
`;

const DetailsWrapper = styled.div`
	position: relative;
	.resource-menu-container {
		top: -${layout.smallSpace};
		right: 0;
	}
	.details-texts {
		display: flex;
		flex-direction: column;
		padding-right: ${layout.largeSpace};
	}
`;

interface TileFooterProps extends WithModalsProps {
	resource: Resource;
	favoriteRef: React.RefObject<HTMLInputElement>;
}

export const DetailedDesktopTileFooter: React.FC<TileFooterProps> = ({
	resource,
	showModal,
	favoriteRef,
}) => {
	const {
		isLoading,
		lastLaunchedTime,
		sessionStartTime,
		summaryState,
		maintenanceModeUserString,
		isUnderMaintenanceOrUnavailable,
	} = useDesktopDetails(resource);
	const contextMenu = useResourceContextMenuBuilder({
		resource,
		favoriteRef,
		showModal,
	});
	const timeStatusText = React.useMemo(() => {
		if (summaryState === SummaryState.IN_USE && sessionStartTime) {
			const duration = moment(sessionStartTime).locale(detectLanguage()).fromNow(true);
			return t('Workspace:active_for', { duration });
		}
		if (!lastLaunchedTime) {
			return null;
		}
		const openedTime = moment(lastLaunchedTime).locale(detectLanguage()).fromNow();
		return t('Workspace:opened_at', { time: openedTime });
	}, [lastLaunchedTime, summaryState, sessionStartTime]);

	return (
		<FooterWrapper shouldGrayOut={isUnderMaintenanceOrUnavailable}>
			<DetailsWrapper>
				<div className="details-texts">
					<TitleText data-testid="detailed-desktop-tile-name">{resource.name}</TitleText>
					<SubTitleText data-testid="detailed-desktop-tile-hostname">
						{resource.desktophostname}
					</SubTitleText>

					<LastOpenedText data-testid="detailed-desktop-tile-session-time">
						{isLoading ? <LoadingBar width="60%" /> : timeStatusText}
					</LastOpenedText>
				</div>

				<ResourceMenu resource={resource} contextMenuBuilder={() => contextMenu} />
			</DetailsWrapper>
			{maintenanceModeUserString && !isLoading && (
				<MaintenanceWrapper data-testid="detailed-desktop-tile-maintenance-reason">
					<IInCircleSolidIcon color={palette.blue800} width={12} height={14} />
					<MaintenanceModeReason>{maintenanceModeUserString}</MaintenanceModeReason>
				</MaintenanceWrapper>
			)}
		</FooterWrapper>
	);
};
